export const canUserEditArtist = (
  user:
    | Pick<LoggedInUser, "id" | "isAdmin" | "editableArtistIds">
    | null
    | undefined,
  artist: { id?: number; userId?: number } | null | undefined,
  { allowAdmin = true }: { allowAdmin?: boolean } = {}
): boolean => {
  if (!user || !artist) {
    return false;
  }
  if (allowAdmin && user.isAdmin) {
    return true;
  }
  return (
    artist.userId === user.id ||
    (artist.id !== undefined && !!user.editableArtistIds?.includes(artist.id))
  );
};

export const getPaidTierCount = (artist: {
  subscriptionTiers: { isDefaultTier: boolean }[];
}) => artist.subscriptionTiers.filter((tier) => !tier.isDefaultTier).length;

export const isTipOnlyArtist = (artist: {
  subscriptionTiers: { isDefaultTier: boolean }[];
  properties?: { showTipOnSupportPage?: boolean };
}) =>
  getPaidTierCount(artist) === 0 && !!artist.properties?.showTipOnSupportPage;

export const getArtistUrlReference = (artist: {
  urlSlug?: string;
  id?: number;
}) => {
  if (artist.urlSlug) {
    return `${artist.urlSlug.toLowerCase()}`;
  }
  return `${artist.id}`;
};

export const getTrackGroupUrlReference = (trackGroup: {
  urlSlug?: string;
  id: number;
}) => {
  if (!!trackGroup.urlSlug) {
    return trackGroup.urlSlug.toLowerCase();
  }
  return `${trackGroup.id}`;
};

export const isTrackGroupPublished = (trackGroup: {
  publishedAt?: string | null;
}) => {
  return (
    !!trackGroup.publishedAt && new Date(trackGroup.publishedAt) <= new Date()
  );
};

export const getArtistUrl = (artist: { urlSlug?: string; id?: number }) => {
  return `/${getArtistUrlReference(artist)}`;
};

export const buildCheckoutCompletePath = (
  artist: { urlSlug?: string; id?: number },
  params: Record<string, string>
) => {
  return `${encodeURI(getArtistUrl(artist))}/checkout-complete?${new URLSearchParams(params).toString()}`;
};

export const getArtistManageUrl = (artistId: number) => {
  return `/manage/artists/${artistId}`;
};

export const getArtistTiersUrl = (artist: {
  urlSlug?: string;
  id?: number;
}) => {
  return `/${getArtistUrlReference(artist)}/support`;
};

export const TIERS_VIEW_ALL_SEARCH = "?view=all";

export const getArtistAllTiersUrl = (artist: {
  urlSlug?: string;
  id?: number;
}) => {
  return `${getArtistTiersUrl(artist)}${TIERS_VIEW_ALL_SEARCH}`;
};

export const getArtistTierUrl = (
  artist: { urlSlug?: string; id?: number },
  tier: { id: number; urlSlug?: string | null }
) => {
  return `${getArtistTiersUrl(artist)}/${tier.urlSlug ?? tier.id}`;
};

export const getArtistManageTiersUrl = (artistId: number) => {
  return getArtistManageUrl(artistId) + "/tiers";
};

export const getArtistManageMerchUrl = (artistId: number, merchId: string) => {
  return `/manage/artists/${artistId}/merch/${merchId}`;
};

export const getTrackGroupWidget = (trackGroupId: number) => {
  return `${import.meta.env.VITE_CLIENT_DOMAIN}/widget/trackGroup/${trackGroupId}`;
};

export const getTrackWidget = (trackId: number) => {
  return `${import.meta.env.VITE_CLIENT_DOMAIN}/widget/track/${trackId}`;
};

export const getLabelWidget = (labelId: number) => {
  return `${import.meta.env.VITE_CLIENT_DOMAIN}/widget/label/${labelId}`;
};

export const getReleaseUrl = (
  artist: { urlSlug?: string; id: number },
  trackGroup: { urlSlug?: string; id: number }
) => {
  return `/${getArtistUrlReference(artist)}/release/${getTrackGroupUrlReference(
    trackGroup
  )}`;
};

export const getManageReleaseUrl = (
  artist: { urlSlug?: string; id: number },
  trackGroup: { urlSlug?: string; id: number }
) => {
  return `${getArtistManageUrl(artist.id)}/release/${trackGroup.id}`;
};

export const getTrackUrl = (
  artist: { urlSlug?: string; id: number },
  trackGroup: { urlSlug?: string; id: number },
  track: { id: number }
) => {
  return `/${getArtistUrlReference(artist)}/release/${getTrackGroupUrlReference(
    trackGroup
  )}/tracks/${track.id}`;
};

export const getMerchUrl = (
  artist: { urlSlug?: string; id: number },
  merch: { id: string; urlSlug?: string | null }
) => {
  return `/${getArtistUrlReference(artist)}/merch/${merch.urlSlug ?? merch.id}`;
};

export const getPostURLReference = (post: Post) => {
  return post.artist
    ? `/${getArtistUrlReference(post.artist)}/posts/${post.urlSlug ?? post.id}/`
    : `/post/${post.id}`;
};

export const getManagePostURLReference = (post: Post) => {
  if (post.artistId) {
    return `${getArtistManageUrl(post.artistId)}/post/${post.id}/`;
  }
  return "";
};
